import { PageHeader, PageShell } from '@/components/PageShell';
import { EventChangesComparison } from '@/app/domainEvents/EventChangesComparison';
import { JsonViewer } from '@/components/JsonViewer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { domainEventState } from '@/state/domainEvents.state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { DomainEventId } from '@rock/types';
import { type FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';

const DomainEventDetailsPage: FC = () => {
    const { eventId: rawEventId } = useParams<{ eventId: string }>();
    const eventId = useMemo(
        () => (rawEventId ? (decodeURIComponent(rawEventId) as DomainEventId) : ('' as DomainEventId)),
        [rawEventId],
    );

    const [event, eventLoadable] = useLoadableAtomValue(domainEventState(eventId));
    const isLoading = eventLoadable.state === 'loading';
    const hasError = eventLoadable.state === 'hasError';

    return (
        <PageShell width="medium">
            <PageHeader title="Domain Event Details" id={eventId} backLabel="Back to domain events" />

            {isLoading ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Loading event...</CardTitle>
                        <CardDescription>Please wait while we fetch the domain event.</CardDescription>
                    </CardHeader>
                </Card>
            ) : hasError ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Error loading event</CardTitle>
                        <CardDescription>Unable to load the domain event. Please try again later.</CardDescription>
                    </CardHeader>
                </Card>
            ) : !event ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Event not found</CardTitle>
                        <CardDescription>
                            We could not find a document for this event. It may have been removed.
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <>
                    <EventChangesComparison event={event} />
                    <Card>
                        <CardHeader>
                            <CardTitle>Domain Event JSON</CardTitle>
                            <CardDescription>The complete domain event payload stored in Firestore.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="max-h-[600px] overflow-auto rounded-md bg-muted p-4">
                                <JsonViewer data={event} />
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </PageShell>
    );
};

export default DomainEventDetailsPage;
